import React from 'react';
import clsx from 'clsx';
import {
  BorderStyle,
  BorderWidth,
  Colors,
  Duration,
  Easing,
  FontSize,
  FontWeight,
  Opacity,
  Radius,
  Spacing,
} from '@common/tokens';
import { LoadingIcon } from './_loading-icon';
import { ErrorHandler } from '@common/components';

type ButtonKindType = 'primary' | 'secondary' | 'tertiary' | 'dashed' | 'link';
type ButtonSizeType = 'sm' | 'md' | 'lg';
type ButtonShapeType = 'default' | 'circle' | 'round';

const iconGap = `${Spacing.sm}px`;

export function Button({
  id,
  kind = 'secondary',
  primary = false,
  size = 'lg',
  shape = 'default',
  danger = false,
  ghost = false,
  block = false,
  disabled = false,
  loading = false,
  icon,
  iconRight,
  htmlType = 'button',
  className,
  onClick,
  children,
  ariaLabel,
  dataTestId,
  ...props
}: {
  id?: string;
  kind?: ButtonKindType;
  primary?: boolean;
  size?: ButtonSizeType;
  shape?: ButtonShapeType;
  danger?: boolean;
  ghost?: boolean;
  block?: boolean;
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  htmlType?: 'button' | 'submit' | 'reset';
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  children?: React.ReactNode;
  ariaLabel?: string;
  dataTestId?: string;
}) {
  const iconOnly = (!!icon || !!iconRight) && !children;

  if (iconOnly && !ariaLabel) {
    console.error(
      'Button: icon-only buttons should have an ariaLabel for accessibility.'
    );
    ErrorHandler.showErrorNotification();
    return null;
  }

  const cls = clsx(
    'wk-button',
    (primary || kind === 'primary') && 'wk-button--primary',
    kind !== 'secondary' && kind !== 'primary' && `wk-button--${kind}`,
    size !== 'lg' && `wk-button--${size}`,
    shape !== 'default' && `wk-button--${shape}`,
    {
      'wk-button--danger': danger,
      'wk-button--ghost': ghost,
      'wk-button--block': block,
      'wk-button--loading': loading,
      'wk-button--disabled': disabled,
      'wk-button--icon-only': iconOnly,
    },
    className
  );

  const handleClick = (e: React.MouseEvent) => {
    if (disabled || loading) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  return (
    <>
      <button
        {...props}
        id={id}
        className={cls}
        type={htmlType}
        data-testid={dataTestId}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-busy={loading ? 'true' : undefined}
        onClick={handleClick}
      >
        <ButtonContent
          icon={icon}
          iconRight={iconRight}
          iconOnly={iconOnly}
          loading={loading}
          size={size}
        >
          {children}
        </ButtonContent>
      </button>
      <style jsx>{`
        .wk-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-sizing: border-box;
          border: ${BorderWidth.sm} ${BorderStyle.solid} transparent;
          border-radius: ${Radius.sm};
          font-weight: ${FontWeight.bold};
          font-family: inherit;
          line-height: 1;
          cursor: pointer;
          text-decoration: none;
          transition: all ${Duration.normal};
          box-shadow: none;
          outline: none;
          white-space: nowrap;
          user-select: none;
          padding: 0 ${Spacing.md}px;
          height: ${Spacing.xxl}px;
          font-size: ${FontSize.md};
          background-color: ${Colors.white};
          border-color: ${Colors.elmLighter};
          color: ${Colors.elm};
        }
        .wk-button:hover,
        .wk-button:focus-visible {
          border-color: ${Colors.elm};
          color: ${Colors.elm};
        }
        .wk-button:active {
          border-color: ${Colors.elm};
          color: ${Colors.elm};
          box-shadow: 0 0 0 3px ${Colors.elmLightest};
        }
        .wk-button:focus-visible {
          outline: ${BorderWidth.md} ${BorderStyle.solid} ${Colors.elm};
          outline-offset: ${Spacing.xxs}px;
        }

        .wk-button--sm {
          height: ${Spacing.lg}px;
          padding: 0 ${Spacing.sm}px;
          font-size: ${FontSize.sm};
        }
        .wk-button--md {
          height: ${Spacing.xl}px;
          padding: 0 ${Spacing.md}px;
          font-size: ${FontSize.sm};
        }

        .wk-button--circle {
          border-radius: ${Radius.full};
          padding: 0;
        }
        .wk-button--round {
          border-radius: 40px;
        }

        .wk-button--icon-only {
          aspect-ratio: 1;
          padding: 0;
        }
        .wk-button--block {
          display: flex;
          width: 100%;
        }

        .wk-button--primary {
          background-color: ${Colors.elm};
          border-color: ${Colors.elm};
          color: ${Colors.white};
        }
        .wk-button--primary:hover,
        .wk-button--primary:focus-visible {
          background-color: ${Colors.elm};
          border-color: ${Colors.elm};
          color: ${Colors.white};
          opacity: ${Opacity.high};
        }
        .wk-button--primary:active {
          background-color: ${Colors.elmDarkest};
          border-color: ${Colors.elmDarkest};
          color: ${Colors.white};
        }

        .wk-button--dashed {
          background-color: ${Colors.white};
          border-color: ${Colors.elmLighter};
          border-style: ${BorderStyle.dashed};
          color: ${Colors.elm};
        }
        .wk-button--dashed:hover,
        .wk-button--dashed:focus-visible {
          border-color: ${Colors.elm};
          color: ${Colors.elm};
        }
        .wk-button--dashed:active {
          border-color: ${Colors.elmDarker};
          color: ${Colors.elmDarker};
        }

        .wk-button--link {
          display: inline-block;
          background-color: transparent;
          border-color: transparent;
          color: ${Colors.elm};
          padding: 0;
          line-height: 20px;
          height: auto;
        }
        .wk-button--link:hover,
        .wk-button--link:focus-visible {
          color: ${Colors.elmDarker};
          border-color: transparent;
        }
        .wk-button--link:active {
          color: ${Colors.elmDarkest};
          border-color: transparent;
        }

        .wk-button--tertiary {
          background-color: transparent;
          border-color: transparent;
          color: ${Colors.elm};
        }
        .wk-button--tertiary:hover,
        .wk-button--tertiary:focus-visible {
          color: ${Colors.elmDarker};
        }
        .wk-button--tertiary:active {
          color: ${Colors.elmDarkest};
        }

        .wk-button--danger {
          border-color: ${Colors.coralLightest};
          color: ${Colors.coral};
        }
        .wk-button--danger:hover,
        .wk-button--danger:focus-visible {
          border-color: ${Colors.coral};
          color: ${Colors.coral};
        }
        .wk-button--danger:active {
          border-color: ${Colors.coralDarkest};
          color: ${Colors.coralDarkest};
        }

        .wk-button--primary.wk-button--danger {
          background-color: ${Colors.coral};
          border-color: ${Colors.coral};
          color: ${Colors.white};
        }
        .wk-button--primary.wk-button--danger:hover,
        .wk-button--primary.wk-button--danger:focus-visible {
          background-color: ${Colors.coralDarker};
          border-color: ${Colors.coralDarker};
          color: ${Colors.white};
        }
        .wk-button--primary.wk-button--danger:active {
          background-color: ${Colors.coralDarkest};
          border-color: ${Colors.coralDarkest};
          color: ${Colors.white};
        }

        .wk-button--link.wk-button--danger {
          color: ${Colors.coral};
          border-color: transparent;
        }
        .wk-button--link.wk-button--danger:hover,
        .wk-button--link.wk-button--danger:focus-visible {
          color: ${Colors.coralDarker};
          border-color: transparent;
        }
        .wk-button--link.wk-button--danger:active {
          color: ${Colors.coralDarkest};
          border-color: transparent;
        }

        .wk-button--tertiary.wk-button--danger {
          color: ${Colors.coral};
          border-color: transparent;
        }
        .wk-button--tertiary.wk-button--danger:hover,
        .wk-button--tertiary.wk-button--danger:focus-visible {
          color: ${Colors.coral};
          border-color: ${Colors.coralLighter};
        }
        .wk-button--tertiary.wk-button--danger:active {
          color: ${Colors.coralDarkest};
        }

        .wk-button--ghost {
          background-color: transparent;
          border-color: ${Colors.white};
          color: ${Colors.white};
        }
        .wk-button--ghost:hover,
        .wk-button--ghost:focus-visible {
          border-color: ${Colors.white};
          color: ${Colors.white};
          opacity: ${Opacity.high};
        }

        .wk-button--primary.wk-button--ghost,
        .wk-button--primary.wk-button--ghost:hover,
        .wk-button--primary.wk-button--ghost:focus-visible,
        .wk-button--primary.wk-button--ghost:active {
          background-color: transparent;
          border-color: ${Colors.elm};
          color: ${Colors.elm};
        }

        .wk-button--ghost.wk-button--danger,
        .wk-button--ghost.wk-button--danger:hover,
        .wk-button--ghost.wk-button--danger:focus-visible,
        .wk-button--ghost.wk-button--danger:active {
          background-color: transparent;
          border-color: ${Colors.coral};
          color: ${Colors.coral};
        }

        .wk-button--disabled,
        .wk-button--disabled:hover,
        .wk-button--disabled:focus-visible,
        .wk-button--disabled:active,
        .wk-button:disabled,
        .wk-button:disabled:hover,
        .wk-button:disabled:focus-visible,
        .wk-button:disabled:active {
          background-color: ${Colors.steelLightest};
          border-color: ${Colors.steel};
          color: ${Colors.midnightLightest};
          cursor: not-allowed;
          pointer-events: none;
        }

        .wk-button--link.wk-button--disabled,
        .wk-button--link.wk-button--disabled:hover,
        .wk-button--link.wk-button--disabled:focus-visible,
        .wk-button--link.wk-button--disabled:active,
        .wk-button--link:disabled,
        .wk-button--link:disabled:hover,
        .wk-button--link:disabled:focus-visible,
        .wk-button--link:disabled:active {
          background-color: transparent;
          border-color: transparent;
        }

        .wk-button--loading {
          cursor: default;
          opacity: ${Opacity.high};
        }

        .wk-button--primary:active:not(.wk-button--link):not(
            .wk-button--tertiary
          ) {
          box-shadow: 0 0 10px 3px ${Colors.elm}20;
        }
        .wk-button--danger:active:not(.wk-button--link) {
          box-shadow: 0 0 10px 3px ${Colors.coral}20;
        }
        .wk-button--ghost:active {
          box-shadow: 0 0 10px 3px ${Colors.white}20;
        }
        .wk-button--link:active {
          box-shadow: none;
        }
      `}</style>
    </>
  );
}

function ButtonContent({
  icon,
  iconRight,
  iconOnly,
  loading,
  size,
  children,
}: {
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  iconOnly: boolean;
  loading: boolean;
  size: ButtonSizeType;
  children?: React.ReactNode;
}) {
  const leftIconNode = icon && loading ? <LoadingIcon size={size} /> : icon;

  const spinnerClasses = clsx(
    'wk-button__spinner',
    loading && 'wk-button__spinner--visible'
  );

  return (
    <>
      {icon && <span className="wk-button__icon">{leftIconNode}</span>}
      {!icon && !iconOnly && (
        <span className={spinnerClasses}>
          <LoadingIcon size={size} />
        </span>
      )}
      {children && <span className="wk-button__content">{children}</span>}
      {iconRight && <span className="wk-button__icon">{iconRight}</span>}
      <style jsx>{`
        .wk-button__icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .wk-button__spinner {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          max-width: 0;
          opacity: 0;
          margin-right: 0;
          overflow: hidden;
          transition:
            max-width ${Duration.normal} ${Easing.ease},
            opacity ${Duration.normal} ${Easing.ease},
            margin-right ${Duration.normal} ${Easing.ease};
        }
        .wk-button__spinner--visible {
          max-width: ${Spacing.lg}px;
          opacity: 1;
          margin-right: ${iconGap};
        }

        .wk-button__content {
          display: inline-flex;
          align-items: center;
        }

        .wk-button__icon + .wk-button__content,
        .wk-button__content + .wk-button__icon {
          margin-left: ${iconGap};
        }
      `}</style>
    </>
  );
}
